import { Alert, Button, Link as MuiLink, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { moduleOfferGateLabels } from "@bw/shared/components";
import { formatMoney } from "@bw/shared/utils";
import type { FeePolicyPublicationFactsProps } from "./feePublicationTypes";
import { fractionToPercent } from "../contractTermsDraft";

export function FeePolicyPublicationFacts({
  review, draft, attempt, isPending, isRetry, error, onPublish,
}: FeePolicyPublicationFactsProps) {
  const exact = review.candidate.kind === "fee_policy" &&
    review.candidate.draft_id === draft.draft_id &&
    review.candidate.draft_revision === draft.revision &&
    review.candidate.audience_org_id === draft.audience_org_id &&
    attempt.expected_revision === review.candidate.draft_revision &&
    attempt.environment === review.candidate.environment;
  const ready = exact && review.source_available && review.environment_allowed === true &&
    !review.unresolved_gates.length && !review.blockers.length;
  return (
    <Stack spacing={1.5}>
      <Typography variant="body2">
        Draft revision {review.candidate.draft_revision} · {review.candidate.environment}
      </Typography>
      <Typography variant="body2">
        Audience: {draft.audience_org_id === null ? "All organizations" : (
          <MuiLink component={Link} to={`/organizations/${draft.audience_org_id}`}>
            {draft.audience_org_id}
          </MuiLink>
        )}
      </Typography>
      <Typography variant="body2">
        Effective {new Date(draft.effective_from).toLocaleString()}
        {draft.effective_until ? ` until ${new Date(draft.effective_until).toLocaleString()}` : ""}.
      </Typography>
      {draft.rules.map((rule, index) => (
        <Stack key={index} spacing={0.5}>
          <Typography variant="subtitle2">
            {rule.component === "bw_commission" ? "BW commission" : "Acquisition fee"}
          </Typography>
          <Typography variant="body2">
            {rule.selector.service_key} · {rule.selector.origin} · {rule.selector.purpose}
          </Typography>
          <Typography variant="body2">
            {fractionToPercent(String(rule.percentage))}%
            {rule.flat_fee ? ` + ${formatMoney(rule.flat_fee.amount_minor / 100, rule.flat_fee.currency)} flat fee` : " · No flat fee"}
            {rule.cap ? ` · Cap ${formatMoney(rule.cap.amount_minor / 100, rule.cap.currency)}` : " · No cap"}
          </Typography>
          <Typography variant="body2">
            Campaign stacking: {rule.campaign_stacking === "exclusive" ? "Exclusive" : "Add after commission"} · Rounding: half up
          </Typography>
        </Stack>
      ))}
      {!review.source_available ? (
        <Alert severity="warning">Publication approval source unavailable. Reviewed source artifacts are required.</Alert>
      ) : null}
      {review.blockers.includes("commercial_publication_environment_unavailable") ? (
        <Alert severity="warning">This publication environment is unavailable.</Alert>
      ) : null}
      {review.blockers.includes("commercial_publication_approval_unresolved") ? (
        <Alert severity="warning">Publication approvals remain unresolved.</Alert>
      ) : null}
      {review.unresolved_gates.map((gate) => (
        <Typography key={gate} variant="body2">
          Unresolved: {moduleOfferGateLabels[gate] ?? "Source decision"}
        </Typography>
      ))}
      {error ? <Alert severity="warning">{error}</Alert> : null}
      {ready ? (
        <Button size="small" variant="contained" disabled={isPending}
          onClick={() => { if (!isPending) onPublish(attempt); }}>
          {isRetry ? "Retry original publication" : "Publish exact reviewed revision"}
        </Button>
      ) : null}
    </Stack>
  );
}
